import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_THEME_BACKGROUND,
  DEFAULT_THEME_ID,
  DEFAULT_THEME_MODE,
  isToggleAwareThemeId,
  resolveTheme,
} from "./themes";
import type { ThemeBackground, ThemeMode } from "./themes";
import { Theme } from "./types";

interface ThemeContextValue {
  theme: Theme;
  themeId: string;
  /** id + mode + background. Changes on ANY theme dimension so chart blocks re-render (and thus
   *  re-create Chart.js instances) on a light/dark or background flip, not just a palette change. */
  themeSignature: string;
  setThemeId: (themeId: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Initial theme ID (e.g., from saved dashboard config) */
  initialThemeId?: string | null;
  /** Selector mode toggle (light/dark). Defaults to light. */
  mode?: ThemeMode;
  /** Selector background toggle (gradient on/off). Defaults to on. */
  background?: ThemeBackground;
  /** Callback when theme changes (for persisting to backend) */
  onThemeChange?: (themeId: string) => void;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialThemeId,
  mode = DEFAULT_THEME_MODE,
  background = DEFAULT_THEME_BACKGROUND,
  onThemeChange,
}) => {
  const [themeId, setThemeIdState] = useState(initialThemeId ?? DEFAULT_THEME_ID);

  // Sync internal state when initialThemeId prop changes (e.g., from parent state update)
  useEffect(() => {
    if (initialThemeId) {
      setThemeIdState(initialThemeId);
    }
  }, [initialThemeId]);

  const setThemeId = useCallback(
    (newThemeId: string) => {
      setThemeIdState(newThemeId);
      onThemeChange?.(newThemeId);
    },
    [onThemeChange]
  );

  const theme = useMemo(() => resolveTheme(themeId, mode, background), [themeId, mode, background]);

  // Legacy slug themes ignore the toggles (resolveTheme returns the identical theme), so their
  // signature must not embed mode/background — otherwise a toggle flip tears down and recreates
  // every Chart.js instance for a visually identical result.
  const themeSignature = isToggleAwareThemeId(themeId)
    ? `${themeId}:${mode}:${background}`
    : themeId;

  const value = useMemo(
    () => ({
      theme,
      themeId,
      themeSignature,
      setThemeId,
    }),
    [theme, themeId, themeSignature, setThemeId]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Hook to access the current theme and theme setter.
 * Must be used within a ThemeProvider.
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

/**
 * Hook to access theme if available, or return undefined if not in a ThemeProvider.
 * Useful for components that may be rendered outside the theme context.
 */
export const useThemeOptional = (): ThemeContextValue | null => {
  return useContext(ThemeContext);
};
